const TARGET_HEIGHT = 1080;
const VIDEO_BPS = 7_000_000;
const AUDIO_BPS = 128_000;
const MIN_COMPRESS_BYTES = 8 * 1024 * 1024;
const SMALL_BYTES_PER_SEC = 3_400_000; // ~27 Mbps gate, proportional to the 7 Mbps target

function pickMimeType() {
  // Only compress when an MP4 recording type is supported (webm output broke Safari playback)
  const candidates = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=avc1',
    'video/mp4',
  ];
  return candidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || null;
}

export async function capturePosterFrame(file) {
  try {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = url;
    video.playsInline = true;
    video.muted = true;
    video.preload = 'auto';
    try {
      await loadMetadata(video);
    } catch {
      URL.revokeObjectURL(url);
      return null;
    }
    await new Promise((resolve, reject) => {
      video.onseeked = () => resolve();
      video.onerror = () => reject(new Error('seek failed'));
      video.currentTime = Math.min(0.1, (video.duration || 1) / 2);
      setTimeout(() => reject(new Error('seek timeout')), 8000);
    }).catch(() => null);
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      URL.revokeObjectURL(url);
      return null;
    }
    const scale = Math.min(720 / w, 1);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.72);
    URL.revokeObjectURL(url);
    return dataUrl && dataUrl.length > 100 ? dataUrl : null;
  } catch {
    return null;
  }
}

function loadMetadata(video) {
  return new Promise((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error('decode failed'));
    setTimeout(() => reject(new Error('metadata timeout')), 15000);
  });
}

export async function compressVideoTo720p(file, onProgress) {
  try {
    if (!window.MediaRecorder || file.size < MIN_COMPRESS_BYTES) return file;
    const mimeType = pickMimeType();
    if (!mimeType) return file;

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.src = url;
    video.playsInline = true;
    video.muted = true; // required for iOS Safari to allow scripted (non-gesture) playback
    video.preload = 'auto';
    try {
      await loadMetadata(video);
    } catch {
      URL.revokeObjectURL(url);
      return file;
    }
    const { videoWidth: w, videoHeight: h, duration } = video;
    if (!w || !h || !duration || !isFinite(duration)) {
      URL.revokeObjectURL(url);
      return file;
    }
    const alreadySmall = Math.min(w, h) <= TARGET_HEIGHT && file.size / duration < SMALL_BYTES_PER_SEC;
    if (alreadySmall) {
      URL.revokeObjectURL(url);
      return file;
    }

    const scale = Math.min(TARGET_HEIGHT / Math.min(w, h), 1);
    const cw = Math.round((w * scale) / 2) * 2;
    const ch = Math.round((h * scale) / 2) * 2;
    const canvas = document.createElement('canvas');
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext('2d');

    const stream = canvas.captureStream(30);
    let audioCtx = null;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const src = audioCtx.createMediaElementSource(video);
      const dest = audioCtx.createMediaStreamDestination();
      src.connect(dest);
      dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
    } catch {}

    const recorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: VIDEO_BPS,
      audioBitsPerSecond: AUDIO_BPS,
    });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };

    const result = await new Promise((resolve, reject) => {
      let raf;
      const draw = () => {
        ctx.drawImage(video, 0, 0, cw, ch);
        if (onProgress && duration) onProgress(Math.min(99, Math.round((video.currentTime / duration) * 100)));
        if (!video.ended && !video.paused) raf = requestAnimationFrame(draw);
      };
      video.onended = () => {
        cancelAnimationFrame(raf);
        try { recorder.stop(); } catch (e) { reject(e); }
      };
      recorder.onstop = () => resolve(new Blob(chunks, { type: mimeType.split(';')[0] }));
      recorder.onerror = (e) => reject(e.error || new Error('recorder error'));
      recorder.start(1000);
      video.play().then(draw).catch(reject);
      setTimeout(() => reject(new Error('compression timeout')), (duration + 60) * 1000);
    }).finally(() => {
      URL.revokeObjectURL(url);
      if (audioCtx) audioCtx.close().catch(() => {});
    });

    if (!result || result.size === 0 || result.size >= file.size) return file;
    return new File([result], file.name.replace(/\.[^.]+$/, '') + '-1080p.mp4', { type: result.type });
  } catch {
    return file;
  }
}
